/**
 * Parches de saneamiento musical legacy + delegación al contrato de tokens.
 * @see ./tokens.ts
 */
import type { MusicGenerationPrefs, MusicalToken, RhythmicPatternPayload } from '../types/music';
import { DEFAULT_MUSIC_PREFS } from '../types/music';
import {
  buildMusicLlmRules,
  normalizeRhythmPattern,
  normalizeTokenArray,
  patternToTeacherScript,
  tokenBeats,
  tokenToFigure,
  tokenToLabel,
  tokenToSymbol,
  tokensToOrderItems,
  isMusicalToken,
  humanizeMusicText,
  formatMusicOption,
  type RhythmFigure,
} from './tokens';
import {
  buildBarBeatStructure,
  rhythmicCellsToLegacyPayload,
} from './rhythmicCellEngine';

export {
  buildMusicLlmRules,
  normalizeTokenArray,
  normalizeRhythmPattern,
  tokenToSymbol,
  tokenToLabel,
  tokenToFigure,
  tokenBeats,
  renderPatternCells,
  patternToTeacherScript,
  tokensToOrderItems,
  TOKEN_SYMBOL,
  CHILEAN_FIGURE_LABEL,
  FIGURE_BEATS_4_4,
  isMusicalToken,
  tokensToSymbolStrip,
  tryParseTokenSequence,
  humanizeMusicText,
  formatMusicOption,
} from './tokens';

/** @deprecated Usar buildMusicLlmRules(prefs) */
export const MUSICA_LLM_RULES = buildMusicLlmRules(DEFAULT_MUSIC_PREFS);

export const FIGURE_SYMBOL: Record<RhythmFigure, string> = {
  whole: '𝅝',
  half: '𝅗𝅥',
  quarter: '♩',
  'eighth-pair': '♫',
  'sixteenth-quad': '♬',
  'rest-quarter': '𝄽',
};

export function inferFigureFromSyllable(syllable: string): RhythmFigure {
  const key = syllable.toLowerCase().trim();
  if (/ta-ka-di-mi|takadimi|ti-ki-ti-ki/.test(key)) return 'sixteenth-quad';
  if (/ta-di|tadi|ti-ti/.test(key)) return 'eighth-pair';
  if (/ta-a-a-a|redonda/.test(key)) return 'whole';
  if (/ta-a|blanca/.test(key)) return 'half';
  if (/silencio|rest/.test(key)) return 'rest-quarter';
  return 'quarter';
}

export function sanitizeTakadimiSyllable(raw: string): string {
  let text = String(raw || '').trim();
  text = text.replace(/^☐\s*/, '');
  text = text.replace(/\([^)]*\d[^)]*\)/g, '');
  text = text.replace(/\([^)]*(medio|cuarto|tiempo|beat|negra|blanca|corchea)[^)]*\)/gi, '');
  text = text.replace(/^[♩♫♪▰𝅗𝅥𝅝𝄽\s]+/, '');
  const takadimi = text.match(/\b(Ta-a-a-a|Ta-a|Ta-di|Ta-ka-di-mi|Ta|ti-ti|ti-ki-ti-ki)\b/i);
  if (takadimi) return takadimi[1];
  return text.replace(/\s+/g, ' ').trim();
}

export function sanitizeRhythmItem(raw: string): string {
  const cleaned = sanitizeTakadimiSyllable(raw);
  const onlySymbols = String(raw).replace(/[^♩♫♪▰𝅗𝅥𝅝𝄽]/g, '');
  if (onlySymbols.length === 1) return onlySymbols;
  if (/^▰+$/.test(onlySymbols)) return '♬';
  return cleaned || String(raw).trim();
}

export function formatTeacherScript(
  syllables: string[],
  symbols?: string[],
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): string {
  const tokens = normalizeTokenArray(
    syllables.length ? syllables : symbols?.map(() => 'Q') ?? [],
  );
  if (tokens.length) return patternToTeacherScript(tokens, prefs);
  const sym = symbols?.join(' ') ?? '';
  return `Percuta rítmicamente el siguiente patrón para la sala: | ${sym} |.`;
}

export function stripListenPrompt(text: string): string {
  return String(text || '')
    .replace(/^escucha[^:]*:\s*/i, '')
    .replace(/^escucha\s+(el|la|los|las)\s+/i, '')
    .trim();
}

export function isListenPrompt(text: string): boolean {
  return /^escucha\b/i.test(String(text || '').trim());
}

export function fixAgrupaCategoryLabel(label: string): string {
  const text = String(label || '').trim();
  if (/blanca|ta-a/i.test(text) && /medio\s+tiempo|0\.5|corchea|semicorchea/i.test(text)) {
    return 'Figuras de 2 tiempos (Blanca)';
  }
  return text;
}

export function fixDurationOption(text: string, figureHint?: string): string {
  let t = String(text || '').trim();
  if (/blanca|ta-a/i.test(`${t} ${figureHint || ''}`) && /medio|0\.5|½|corchea/i.test(t)) {
    return t.replace(/medio\s+tiempo/gi, '2 tiempos').replace(/0\.5/g, '2');
  }
  return t.replace(/\((negra|blanca|corchea|semicorchea|redonda)\)/gi, (_, w) =>
    String(w).charAt(0).toUpperCase() + String(w).slice(1),
  );
}

function resolveMusicPrefs(payload: Record<string, unknown>, prefs?: MusicGenerationPrefs): MusicGenerationPrefs {
  const readingMethod = payload.rhythmMethod ?? payload.readingMethod ?? prefs?.readingMethod;
  const graphicMode = payload.graphicMode ?? prefs?.graphicMode;
  return {
    readingMethod:
      readingMethod === 'takadimi' || readingMethod === 'kodaly' ? readingMethod : 'tradicional',
    graphicMode: graphicMode === 'symbols-labels' ? 'symbols-labels' : 'symbols',
  };
}

function rhythmCellsFromTokens(
  pattern: MusicalToken[],
  prefs: MusicGenerationPrefs,
  timeSignature = '4/4',
) {
  const structure = buildBarBeatStructure(pattern, timeSignature);
  const legacy = rhythmicCellsToLegacyPayload(structure.cells);
  return legacy.map((cell) => ({
    ...cell,
    syllable:
      prefs.readingMethod === 'takadimi'
        ? cell.syllable
        : tokenToLabel(cell.token, prefs.readingMethod),
  }));
}

function patchRhythmPatternFields(
  base: Record<string, unknown>,
  prefs: MusicGenerationPrefs,
): RhythmicPatternPayload | undefined {
  const fromField = normalizeRhythmPattern(base.rhythmPattern);
  const fromTokens = normalizeTokenArray(base.patternTokens ?? base.pattern);
  const pattern = fromField?.pattern ?? (fromTokens.length ? fromTokens : undefined);
  if (!pattern?.length) return fromField;

  const timeSignature =
    fromField?.timeSignature ??
    (['4/4', '3/4', '2/4'].includes(String(base.meter || base.timeSignature))
      ? (String(base.meter || base.timeSignature) as RhythmicPatternPayload['timeSignature'])
      : '4/4');

  const missingIndex =
    fromField?.missingIndex ??
    (typeof base.missingIndex === 'number' ? base.missingIndex : undefined);

  const rhythmPattern: RhythmicPatternPayload = { timeSignature, pattern, missingIndex };
  base.rhythmPattern = rhythmPattern;
  base.patternTokens = pattern;
  base.rhythmMethod = prefs.readingMethod;
  base.graphicMode = prefs.graphicMode;
  base.meter = timeSignature;
  if (!Array.isArray(base.beatCells) || !base.beatCells.length) {
    const structure = buildBarBeatStructure(pattern, timeSignature);
    base.beatCells = structure.beatCells;
    base.rhythmicCells = structure.cells;
    base.rhythmCells = rhythmCellsFromTokens(pattern, prefs, timeSignature);
    base.beatsPerBar = structure.beatsPerBar;
  } else {
    base.rhythmCells = rhythmCellsFromTokens(pattern, prefs, timeSignature);
  }
  return rhythmPattern;
}

export function applyMusicPayloadPatch(
  mode: string,
  payload: Record<string, unknown>,
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): Record<string, unknown> {
  const base = { ...payload };
  const musicPrefs = resolveMusicPrefs(base, prefs);

  switch (mode) {
    case 'completar': {
      const rhythm = patchRhythmPatternFields(base, musicPrefs);
      if (rhythm) {
        base.rhythmMethod = musicPrefs.readingMethod;
        base.graphicMode = musicPrefs.graphicMode;
        const missing = rhythm.missingIndex ?? rhythm.pattern.length - 1;
        const missingToken = rhythm.pattern[missing];
        if (missingToken && !/\[[^\]]+\]/.test(String(base.text || ''))) {
          base.text = 'Observa la secuencia rítmica y completa la figura que falta.';
        }
        if (!base.context && !base.referenceExpression) {
          base.context = `Compás ${rhythm.timeSignature}`;
        }
        const blankAnswer = String(
          base.blanks?.[0]?.answer ?? base.blanks?.[0] ?? missingToken ?? '',
        );
        if (!blankAnswer || blankAnswer === 'undefined') {
          base.blanks = [{ id: 'b1', answer: missingToken ?? 'Q' }];
        }
      }
      if (String(base.text || '').match(/:\s*\.\s/)) {
        base.text = 'Completa la figura rítmica que falta en la secuencia.';
      }
      base.text = humanizeMusicText(String(base.text || ''), musicPrefs);
      break;
    }
    case 'order': {
      patchRhythmPatternFields(base, musicPrefs);
      const pattern =
        normalizeRhythmPattern(base.rhythmPattern)?.pattern ??
        normalizeTokenArray(base.patternTokens ?? base.items);
      if (pattern.length >= 2) {
        base.patternTokens = pattern;
        base.items = tokensToOrderItems(pattern, musicPrefs);
        base.printLayout = 'rhythm-bar';
        base.rhythmCells = rhythmCellsFromTokens(pattern, musicPrefs);
        base.meter = String(base.meter || base.rhythmPattern?.timeSignature || '4/4');
        base.rhythmMethod = musicPrefs.readingMethod;
      } else {
        const cleaned = (Array.isArray(base.items) ? base.items : []).map((i) =>
          sanitizeRhythmItem(String(i)),
        );
        base.items = cleaned.filter(Boolean);
        base.printLayout = 'rhythm-bar';
      }
      break;
    }
    case 'dictado': {
      patchRhythmPatternFields(base, musicPrefs);
      const pattern =
        normalizeRhythmPattern(base.rhythmPattern)?.pattern ??
        normalizeTokenArray(base.patternTokens ?? base.patternSyllables);
      if (pattern.length) {
        base.patternTokens = pattern;
        base.patternSymbols = pattern.map(tokenToSymbol);
        base.patternSyllables = pattern.map((t) => tokenToLabel(t, musicPrefs.readingMethod));
        base.teacherScript =
          String(base.teacherScript || '') || patternToTeacherScript(pattern, musicPrefs);
      }
      base.studentPrompt = String(
        base.studentPrompt || 'Transcribe el patrón rítmico usando símbolos o nombres de figuras:',
      );
      if (isListenPrompt(String(base.prompt || ''))) base.prompt = base.studentPrompt;
      base.rhythmMethod = musicPrefs.readingMethod;
      break;
    }
    case 'anatomia': {
      base.rhythmMethod = musicPrefs.readingMethod;
      if (Array.isArray(base.items)) {
        base.items = (base.items as Array<Record<string, unknown>>).map((item, i) => {
          const tokenRaw = item.token ?? item.figureToken;
          const token = isMusicalToken(String(tokenRaw))
            ? (String(tokenRaw) as MusicalToken)
            : normalizeTokenArray([tokenRaw, item.figureLabel, item.symbol])[0] ?? 'Q';
          return {
            id: String(item.id || `a${i + 1}`),
            token,
            symbol: tokenToSymbol(token),
            figureLabel: tokenToLabel(token, musicPrefs.readingMethod),
            correctBeats: Number(item.correctBeats) || tokenBeats(token),
          };
        });
      }
      break;
    }
    case 'arena': {
      if (Array.isArray(base.questions)) {
        base.questions = base.questions.map((q) => {
          const row = { ...(q as Record<string, unknown>) };
          if (row.patternTokens || row.rhythmPattern) {
            patchRhythmPatternFields(row, musicPrefs);
          }
          row.prompt = humanizeMusicText(String(row.prompt || ''), musicPrefs);
          if (row.studentPrompt) {
            row.studentPrompt = humanizeMusicText(String(row.studentPrompt), musicPrefs);
          }
          if (isListenPrompt(String(row.prompt || ''))) {
            row.studentPrompt = 'Transcribe el patrón rítmico que percibes:';
            row.prompt = row.studentPrompt;
            const pattern = normalizeTokenArray(row.patternTokens);
            if (pattern.length) {
              row.teacherScript =
                String(row.teacherScript || '') || patternToTeacherScript(pattern, musicPrefs);
            }
          }
          if (Array.isArray(row.options)) {
            row.options = row.options.map((opt) => formatMusicOption(String(opt), musicPrefs));
          }
          return row;
        });
      }
      break;
    }
    case 'desarrollo': {
      base.prompt = humanizeMusicText(String(base.prompt || ''), musicPrefs);
      if (Array.isArray(base.rubric)) {
        base.rubric = base.rubric.map((line) => humanizeMusicText(String(line), musicPrefs));
      }
      break;
    }
    case 'rhythm_canvas': {
      patchRhythmPatternFields(base, musicPrefs);
      const pattern =
        normalizeRhythmPattern(base.rhythmPattern)?.pattern ??
        normalizeTokenArray(base.patternTokens);
      if (pattern.length) {
        base.patternTokens = pattern;
        base.prompt = humanizeMusicText(String(base.prompt || ''), musicPrefs);
        base.rhythmMethod = musicPrefs.readingMethod;
        base.graphicMode = musicPrefs.graphicMode;
      }
      break;
    }
    case 'melody_staff': {
      base.prompt = humanizeMusicText(String(base.prompt || ''), musicPrefs);
      if (Array.isArray(base.notes)) {
        base.notes = (base.notes as Array<Record<string, unknown>>).map((note, i) => ({
          id: String(note.id || `n${i + 1}`),
          pitch: String(note.pitch || 'G4'),
          duration: isMusicalToken(String(note.duration)) ? note.duration : 'Q',
        }));
      }
      break;
    }
    case 'ear_training': {
      patchRhythmPatternFields(base, musicPrefs);
      const pattern = normalizeTokenArray(base.patternTokens);
      if (pattern.length) {
        base.patternTokens = pattern;
        base.teacherScript =
          String(base.teacherScript || '') || patternToTeacherScript(pattern, musicPrefs);
      }
      base.studentPrompt = humanizeMusicText(String(base.studentPrompt || ''), musicPrefs);
      base.teacherScript = humanizeMusicText(String(base.teacherScript || ''), musicPrefs);
      base.rhythmMethod = musicPrefs.readingMethod;
      break;
    }
    case 'agrupa':
      if (Array.isArray(base.categories)) {
        base.categories = base.categories.map((cat) => ({
          ...(cat as object),
          label: fixAgrupaCategoryLabel(String((cat as Record<string, unknown>).label || '')),
        }));
      }
      if (Array.isArray(base.items)) {
        base.items = base.items.map((item) => ({
          ...(item as object),
          text: fixDurationOption(String((item as Record<string, unknown>).text || '')),
        }));
      }
      break;
    default:
      break;
  }

  return base;
}
